import React, { useState, useEffect } from 'react';
import { X, Printer } from 'lucide-react';
import DeliveryInvoicePrintView from '../../pages/Invoices/DeliveryInvoicePrintView';
import { useSettings } from '../../contexts/SettingsContext';

interface PrintPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: string;
  invoiceType: 'purchase' | 'delivery';
  invoiceData?: any;
  invoicesData?: any[];
  settings?: any;
}

const PrintPreviewModal: React.FC<PrintPreviewModalProps> = ({
  isOpen,
  onClose,
  invoiceId,
  invoiceType,
  invoiceData,
  invoicesData,
  settings
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { settings: globalSettings } = useSettings();

  useEffect(() => {
    if (isOpen && (invoiceData || (invoicesData && invoicesData.length > 0))) {
      setIsLoading(false);
      setError(null);
    }
  }, [isOpen, invoiceData, invoicesData]);



  const handlePrintDirectly = () => {
    try {
      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      
      if (!printWindow) {
        setError('Please allow popups to print the invoice.');
        return;
      }

      // Get the invoice content
      const invoiceContent = document.getElementById('invoice-print-content');
      
      if (!invoiceContent) {
        setError('Invoice content not found.');
        return;
      }

      // Create the print HTML
      const printHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${invoiceType === 'delivery' ? 'Delivery' : 'Purchase'} Invoice</title>
          <style>
            @media print {
              body { margin: 0; padding: 20px; }
              .no-print { display: none !important; }
              .invoice-break { page-break-before: always; break-before: page; }
            }
            body {
              font-family: 'Times New Roman', serif;
              margin: 0;
              padding: 20px;
              background: white;
              color: black;
            }
            .invoice-container {
              max-width: 800px;
              margin: 0 auto;
              background: white;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #000;
              padding-bottom: 20px;
            }
            .company-name {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 10px;
            }
            .invoice-title {
              font-size: 20px;
              font-weight: bold;
              margin-bottom: 10px;
            }
            .invoice-details {
              display: flex;
              justify-content: space-between;
              margin-bottom: 30px;
            }
            .customer-info, .invoice-info {
              flex: 1;
            }
            .items-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 30px;
            }
            .items-table th, .items-table td {
              border: 1px solid #000;
              padding: 8px;
              text-align: left;
            }
            .items-table th {
              background-color: #f0f0f0;
              font-weight: bold;
            }
            .total-section {
              text-align: right;
              margin-top: 20px;
            }
            .total-row {
              font-weight: bold;
              font-size: 16px;
            }
            .footer {
              margin-top: 40px;
              text-align: center;
              font-size: 12px;
              color: #666;
            }
          </style>
        </head>
        <body>
          ${invoiceContent.innerHTML}
        </body>
        </html>
      `;

      // Write the content to the new window
      printWindow.document.write(printHTML);
      printWindow.document.close();

      // Wait for content to load, then print
      printWindow.onload = () => {
        printWindow.print();
        setTimeout(() => {
          printWindow.close();
        }, 1000);
      };

    } catch (err) {
      console.error('Error printing:', err);
      setError('Failed to print. Please try downloading the PDF instead.');
    }
  };

  const handleClose = () => {
    setError(null);
    setIsLoading(false);
    onClose();
  };

  const renderInvoicePreview = () => {
    if (!invoiceData && (!invoicesData || invoicesData.length === 0)) return <div className="text-center text-gray-500">No invoice data available</div>;

    // For delivery invoices, use the DeliveryInvoicePrintView component
    if (invoiceType === 'delivery') {
      const invoicesList = invoicesData && invoicesData.length > 0 ? invoicesData : (invoiceData ? [invoiceData] : []);
      return (
        <div id="invoice-print-content" className="bg-white">
          {invoicesList.map((inv, idx) => (
            <div key={inv._id || idx} className={idx > 0 ? 'invoice-break' : ''} style={idx > 0 ? { pageBreakBefore: 'always', breakBefore: 'page' } : {}}>
              <DeliveryInvoicePrintView 
                invoice={inv}
                settings={globalSettings}
                currentPage={1}
                itemsPerPage={16}
                printMode={true}
              />
            </div>
          ))}
        </div>
      );
    }

    // For purchase invoices, use the generic preview
    return (
      <div id="invoice-print-content" className="bg-white p-8 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Purchase Invoice
            </h1>
            <p className="text-gray-600">Invoice #{invoiceData.invoiceNumber}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Date: {new Date(invoiceData.date || invoiceData.createdAt).toLocaleDateString()}</p>
          </div>
        </div>

        {/* Customer Information */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-2">Customer Information</h2>
          <div className="bg-gray-50 p-4 rounded">
            <p className="font-medium">{invoiceData.customer?.name}</p>
            {invoiceData.customer?.address && <p className="text-gray-600">{invoiceData.customer.address}</p>}
            {invoiceData.customer?.phone && <p className="text-gray-600">Phone: {invoiceData.customer.phone}</p>}
            {invoiceData.customer?.email && <p className="text-gray-600">Email: {invoiceData.customer.email}</p>}
          </div>
        </div>

        {/* Items Table */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-2">Items</h2>
          <div className="border rounded overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">Item</th>
                  <th className="px-4 py-2 text-left">Description</th>
                  <th className="px-4 py-2 text-right">Quantity</th>
                  <th className="px-4 py-2 text-right">Unit Price</th>
                  <th className="px-4 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {invoiceData.items?.map((item: any, index: number) => (
                  <tr key={index} className="border-t">
                    <td className="px-4 py-2">{item.name || item.product?.name}</td>
                    <td className="px-4 py-2">{item.description || item.details}</td>
                    <td className="px-4 py-2 text-right">{item.quantity}</td>
                    <td className="px-4 py-2 text-right">${item.unitPrice?.toFixed(2)}</td>
                    <td className="px-4 py-2 text-right">${item.totalPrice?.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Totals */}
        <div className="flex justify-end">
          <div className="w-64">
            <div className="flex justify-between py-2">
              <span>Subtotal:</span>
              <span>${invoiceData.subtotal?.toFixed(2)}</span>
            </div>
            {invoiceData.discount > 0 && (
              <div className="flex justify-between py-2">
                <span>Discount:</span>
                <span>-${invoiceData.discount?.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between py-2 font-bold border-t">
              <span>Total:</span>
              <span>${invoiceData.total?.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Printer className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Print Preview
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col">
          {/* Toolbar */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center space-x-4">
              <button
                className="px-3 py-1 rounded text-sm font-medium bg-blue-600 text-white"
              >
                Print Layout
              </button>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={handlePrintDirectly}
                disabled={isLoading}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Printer className="w-4 h-4" />
                <span>Print</span>
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 border-b border-red-200">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Content Area */}
          <div className="flex-1 overflow-auto">
            {isLoading && (
              <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center space-y-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="text-gray-600">Processing...</p>
                </div>
              </div>
            )}
            
            {!isLoading && !error && (
              <div className="h-full overflow-auto">
                {renderInvoicePreview()}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintPreviewModal; 